// API-клиент бэк-офиса — базовый HTTP-класс с авторизацией и обновлением токенов.
// Phase 0: только auth-методы. Доменные методы (jobs/items/...) добавляются в Phase 1+.
// Все запросы идут через gateway в существующие ClientApi/Integration API —
// отдельного бэкенда у бэк-офиса нет.

import { mapClientAuthResponse } from './mappers/auth.mapper';
import { mapItem } from './mappers/item.mapper';
import { mapWarehouseFull } from './mappers/place.mapper';
import { mapLocation } from './mappers/location.mapper';
import { mapJobCategory } from './mappers/job-category.mapper';
import type {
  AuthResponseRaw,
  LoginRequest,
  AuthResponse,
  UserProfile,
} from './types/auth.types';
import type {
  JobsListRaw,
  OperationsListRaw,
} from './types/job.types';
import type {
  ItemRaw,
  ItemsListRaw,
  ItemsListDto,
  ItemDto,
} from './types/item.types';
import type {
  WarehouseFullRaw,
  WarehouseFullDto,
} from './types/place.types';
import type {
  LocationRaw,
  LocationDto,
  CreateLocationPayload,
  UpdateLocationPayload,
} from './types/location.types';
import type {
  JobCategoryRaw,
  JobCategoryDto,
} from './types/job-category.types';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://simple.4dev.kz';

/** Расширенная ошибка API с HTTP-статусом и сырым ответом */
export class ApiError extends Error {
  readonly status: number;
  readonly raw: string;

  constructor(message: string, status: number, raw: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.raw = raw;
  }
}

/** Получить HTTP-статус из ошибки (если это ApiError) */
export function getApiErrorStatus(err: unknown): number | undefined {
  if (err instanceof ApiError) return err.status;
  if (typeof (err as Record<string, unknown>)?.status === 'number') {
    return (err as Record<string, unknown>).status as number;
  }
  return undefined;
}

export class ApiClient {
  protected accessToken: string | null = null;
  protected refreshToken: string | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      this.accessToken = localStorage.getItem('accessToken');
      this.refreshToken = localStorage.getItem('refreshToken');
    }
  }

  setTokens(accessToken: string, refreshToken: string) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    if (typeof window !== 'undefined') {
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
    }
  }

  clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
    if (typeof window !== 'undefined') {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    }
  }

  isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  // ===== HTTP-клиент =====

  protected async toApiError(response: Response): Promise<ApiError> {
    const contentType = response.headers.get('content-type') || '';
    const text = await response.text();
    let message = text || response.statusText;

    if (contentType.includes('json')) {
      try {
        const parsed: Record<string, unknown> = JSON.parse(text);
        message = (parsed?.detail || parsed?.title || parsed?.message || message) as string;
      } catch {
        // игнорируем ошибки парсинга
      }
    }

    return new ApiError(message, response.status, text);
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (response.status === 401 && this.refreshToken) {
      const refreshed = await this.refreshAccessToken();
      if (refreshed) {
        headers['Authorization'] = `Bearer ${this.accessToken}`;
        const retryResponse = await fetch(`${API_URL}${endpoint}`, {
          ...options,
          headers,
        });
        if (!retryResponse.ok) {
          throw await this.toApiError(retryResponse);
        }
        if (retryResponse.status === 204) return undefined as T;
        return retryResponse.json();
      }
    }

    if (!response.ok) {
      throw await this.toApiError(response);
    }

    if (response.status === 204) return undefined as T;
    return response.json();
  }

  private async refreshAccessToken(): Promise<boolean> {
    try {
      const response = await fetch(`${API_URL}/api/v1/client/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: this.refreshToken }),
      });

      if (!response.ok) {
        this.clearTokens();
        return false;
      }

      const data = await response.json();
      const mapped = mapClientAuthResponse(data);
      this.setTokens(mapped.accessToken, mapped.refreshToken);
      return true;
    } catch {
      this.clearTokens();
      return false;
    }
  }

  // ===== Auth =====

  async login(data: LoginRequest): Promise<AuthResponse> {
    const result = await this.request<AuthResponseRaw>('/api/v1/client/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    const mapped = mapClientAuthResponse(result);
    this.setTokens(mapped.accessToken, mapped.refreshToken);
    return mapped;
  }

  async logout(): Promise<void> {
    try {
      await this.request('/api/v1/client/auth/logout', { method: 'POST' });
    } finally {
      this.clearTokens();
    }
  }

  async getProfile(): Promise<UserProfile> {
    const result = await this.request<AuthResponseRaw>('/api/v1/client/auth/me');
    const mapped = mapClientAuthResponse({
      access_token: this.accessToken,
      refresh_token: this.refreshToken,
      expires_at: new Date(Date.now() + 60_000).toISOString(),
      user: result?.user,
      organization: result?.organization,
    });
    return mapped.user;
  }

  // ===== Задания =====

  async getJobs(params?: {
    status?: number;
    skip?: number;
    take?: number;
  }): Promise<JobsListRaw> {
    const query = new URLSearchParams();
    if (typeof params?.status === 'number') query.set('status', params.status.toString());
    if (typeof params?.skip === 'number') query.set('skip', params.skip.toString());
    if (typeof params?.take === 'number') query.set('take', params.take.toString());
    const suffix = query.toString() ? `?${query}` : '';
    return this.request<JobsListRaw>(`/api/v1/client/jobs${suffix}`);
  }

  async getJob(id: string): Promise<Record<string, unknown>> {
    return this.request(`/api/v1/client/jobs/${id}`);
  }

  async createJob(data: {
    jobCategoryCode: number;
    description?: string;
    customerName?: string;
    customerId?: string;
    orderNumber?: string;
    warehouseId?: string;
    priority?: number;
    dueAt?: string;
    items?: Array<{ itemId: string; planQty: number; locationId?: string }>;
  }): Promise<Record<string, unknown>> {
    return this.request('/api/v1/client/jobs', {
      method: 'POST',
      body: JSON.stringify({
        job_category_code: data.jobCategoryCode,
        description: data.description ?? null,
        customer_name: data.customerName ?? null,
        customer_id: data.customerId ?? null,
        order_number: data.orderNumber ?? null,
        warehouse_id: data.warehouseId ?? null,
        priority: data.priority ?? 1,
        due_at: data.dueAt ?? null,
        items: Array.isArray(data.items)
          ? data.items.map((i) => ({
              item_id: i.itemId,
              plan_qty: i.planQty,
              location_id: i.locationId ?? null,
            }))
          : null,
      }),
    });
  }

  async cancelJob(id: string): Promise<void> {
    await this.request(`/api/v1/client/jobs/${id}/cancel`, { method: 'POST' });
  }

  async deleteJob(id: string): Promise<void> {
    await this.request(`/api/v1/client/jobs/${id}`, { method: 'DELETE' });
  }

  // ===== Категории заданий =====

  async getJobCategories(): Promise<JobCategoryDto[]> {
    const raw = await this.request<JobCategoryRaw[]>('/api/v1/client/settings/job-categories');
    return (raw ?? []).map(mapJobCategory);
  }

  // ===== Каталоги ТСД =====

  async getCatalogs(): Promise<{ catalogs: Array<{ key: string; title: string; kind: 'static' | 'custom' }> }> {
    return this.request('/api/v1/tsd/catalogs');
  }

  async getTsdCatalogItems(catalogKey: string, take = 200): Promise<{ items: Array<{ id: string; catalogKey: string; itemId: string; title: string; code: string | null; attributes: Record<string, string | null> | null }>; total: number }> {
    const result = await this.request<{ items: Array<{ id: string; catalog_key: string; item_id: string; title: string; code: string | null; attributes: Record<string, string | null> | null }>; total: number; skip: number; take: number }>(
      `/api/v1/client/tsd-catalogs?catalogKey=${encodeURIComponent(catalogKey)}&take=${take}`
    );
    return {
      items: (result?.items ?? []).map((i) => ({
        id: i.id,
        catalogKey: i.catalog_key,
        itemId: i.item_id,
        title: i.title,
        code: i.code ?? null,
        attributes: i.attributes ?? null,
      })),
      total: result?.total ?? 0,
    };
  }

  // ===== Dashboard =====

  async getDashboardStats(): Promise<{
    activeTsdUsers: number;
    totalOperationsToday: number;
    totalJobsActive: number;
    totalItems: number;
    operationsByType: Array<{ typeName: string; count: number }>;
    jobsByCategory: Array<{ categoryName: string; inProgress: number; total: number }>;
  }> {
    const raw = await this.request<{
      active_tsd_users: number;
      total_operations_today: number;
      total_jobs_active: number;
      total_items: number;
      total_categories: number;
      operations_by_type: Array<{ type_name: string; count: number }>;
      jobs_by_category: Array<{ category_name: string; in_progress: number; total: number }>;
    }>('/api/v1/client/dashboard/stats');
    return {
      activeTsdUsers: raw?.active_tsd_users ?? 0,
      totalOperationsToday: raw?.total_operations_today ?? 0,
      totalJobsActive: raw?.total_jobs_active ?? 0,
      totalItems: raw?.total_items ?? 0,
      operationsByType: (raw?.operations_by_type ?? []).map((o) => ({ typeName: o.type_name, count: o.count })),
      jobsByCategory: (raw?.jobs_by_category ?? []).map((c) => ({ categoryName: c.category_name, inProgress: c.in_progress, total: c.total })),
    };
  }

  // ===== Операции =====

  async getOperations(params?: {
    status?: number;
    search?: string;
    skip?: number;
    take?: number;
  }): Promise<OperationsListRaw> {
    const query = new URLSearchParams();
    if (typeof params?.status === 'number') query.set('status', params.status.toString());
    if (params?.search) query.set('search', params.search);
    if (typeof params?.skip === 'number') query.set('skip', params.skip.toString());
    if (typeof params?.take === 'number') query.set('take', params.take.toString());
    const suffix = query.toString() ? `?${query}` : '';
    return this.request<OperationsListRaw>(`/api/v1/client/operations${suffix}`);
  }

  async getOperation(id: string): Promise<Record<string, unknown>> {
    return this.request(`/api/v1/client/operations/${id}`);
  }

  // ===== Товары =====

  async getItems(params?: { search?: string; skip?: number; take?: number }): Promise<ItemsListDto> {
    const search = params?.search ? `&search=${encodeURIComponent(params.search)}` : '';
    const skip = params?.skip ?? 0;
    const take = params?.take ?? 50;
    const raw = await this.request<ItemsListRaw>(
      `/api/v1/client/items?skip=${skip}&take=${take}${search}`
    );
    return {
      items: (raw?.items ?? []).map(mapItem),
      total: raw?.total ?? 0,
      skip: raw?.skip ?? skip,
      take: raw?.take ?? take,
    };
  }

  async getItem(id: string): Promise<ItemDto> {
    const raw = await this.request<ItemRaw>(`/api/v1/client/items/${id}`);
    return mapItem(raw);
  }

  // ===== Склады =====

  async getWarehouses(includeInactive = false): Promise<WarehouseFullDto[]> {
    const raw = await this.request<{ items: WarehouseFullRaw[]; total: number }>(
      `/api/v1/client/warehouses?includeInactive=${includeInactive}&take=500`
    );
    return (raw?.items ?? []).map(mapWarehouseFull);
  }

  async createWarehouse(data: { code: string; name: string; address?: string; useAddressStorage?: boolean }): Promise<WarehouseFullDto> {
    const raw = await this.request<WarehouseFullRaw>('/api/v1/client/warehouses', {
      method: 'POST',
      body: JSON.stringify({
        code: data.code,
        name: data.name,
        address: data.address,
        use_address_storage: data.useAddressStorage ?? false,
      }),
    });
    return mapWarehouseFull(raw);
  }

  async updateWarehouse(id: string, data: { code?: string; name?: string; address?: string | null; useAddressStorage?: boolean; isActive?: boolean }): Promise<WarehouseFullDto> {
    const body: Record<string, unknown> = {};
    if (data.code !== undefined) body.code = data.code;
    if (data.name !== undefined) body.name = data.name;
    if (data.address !== undefined) body.address = data.address;
    if (data.useAddressStorage !== undefined) body.use_address_storage = data.useAddressStorage;
    if (data.isActive !== undefined) body.is_active = data.isActive;
    const raw = await this.request<WarehouseFullRaw>(`/api/v1/client/warehouses/${id}`, { method: 'PUT', body: JSON.stringify(body) });
    return mapWarehouseFull(raw);
  }

  async deleteWarehouse(id: string): Promise<void> {
    await this.request(`/api/v1/client/warehouses/${id}`, { method: 'DELETE' });
  }

  // ===== Ячейки адресного хранения =====

  async getLocations(warehouseId: string, includeInactive = false): Promise<LocationDto[]> {
    const raw = await this.request<{ items: LocationRaw[]; total: number }>(
      `/api/v1/client/locations?warehouseId=${encodeURIComponent(warehouseId)}&includeInactive=${includeInactive}&take=1000`
    );
    return (raw?.items ?? []).map(mapLocation);
  }

  async getLocation(id: string): Promise<LocationDto> {
    const raw = await this.request<LocationRaw>(`/api/v1/client/locations/${id}`);
    return mapLocation(raw);
  }

  async createLocation(data: CreateLocationPayload): Promise<LocationDto> {
    const raw = await this.request<LocationRaw>('/api/v1/client/locations', {
      method: 'POST',
      body: JSON.stringify({
        warehouse_id: data.warehouseId,
        code: data.code,
        zone: data.zone ?? null,
        rack: data.rack ?? null,
        shelf: data.shelf ?? null,
        position: data.position ?? null,
        barcode: data.barcode ?? null,
        type: data.type ?? 'storage',
        is_active: data.isActive ?? true,
      }),
    });
    return mapLocation(raw);
  }

  async updateLocation(id: string, data: UpdateLocationPayload): Promise<LocationDto> {
    const body: Record<string, unknown> = {};
    if (data.code !== undefined) body.code = data.code;
    if (data.zone !== undefined) body.zone = data.zone;
    if (data.rack !== undefined) body.rack = data.rack;
    if (data.shelf !== undefined) body.shelf = data.shelf;
    if (data.position !== undefined) body.position = data.position;
    if (data.barcode !== undefined) body.barcode = data.barcode;
    if (data.type !== undefined) body.type = data.type;
    if (data.isActive !== undefined) body.is_active = data.isActive;
    const raw = await this.request<LocationRaw>(`/api/v1/client/locations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    return mapLocation(raw);
  }

  async deleteLocation(id: string): Promise<void> {
    await this.request(`/api/v1/client/locations/${id}`, { method: 'DELETE' });
  }

  // ===== Категории товаров =====

  async getCategories(): Promise<Array<{ id: string; name: string; parentId?: string | null; parentName?: string | null; sortOrder: number; isActive: boolean }>> {
    const raw = await this.request<{ items: Array<{ id: string; name: string; parent_id?: string | null; parent_name?: string | null; sort_order: number; is_active: boolean }>; total: number }>(
      '/api/v1/client/categories'
    );
    return (raw?.items ?? []).map((c) => ({
      id: c.id, name: c.name, parentId: c.parent_id ?? null, parentName: c.parent_name ?? null,
      sortOrder: c.sort_order ?? 0, isActive: !!c.is_active,
    }));
  }

  async createCategory(data: { name: string; parentId?: string; sortOrder?: number; isActive?: boolean }): Promise<unknown> {
    return this.request('/api/v1/client/categories', {
      method: 'POST',
      body: JSON.stringify({
        name: data.name, parent_id: data.parentId ?? null,
        sort_order: data.sortOrder ?? 0, is_active: data.isActive ?? true,
      }),
    });
  }

  async updateCategory(id: string, data: { name?: string; parentId?: string; sortOrder?: number; isActive?: boolean }): Promise<void> {
    await this.request(`/api/v1/client/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify({
        name: data.name, parent_id: data.parentId ?? null,
        sort_order: data.sortOrder, is_active: data.isActive,
      }),
    });
  }

  async deleteCategory(id: string): Promise<void> {
    await this.request(`/api/v1/client/categories/${id}`, { method: 'DELETE' });
  }

  // ===== TSD-пользователи (для назначения исполнителей) =====

  async getTsdUsers(): Promise<Array<{ id: string; username: string; firstName?: string; lastName?: string; fullName: string }>> {
    const raw = await this.request<{ users: Array<{ id: string; username: string; first_name?: string; last_name?: string }>; total: number }>(
      '/api/v1/client/tsd-users'
    );
    return (raw?.users ?? []).map((u) => ({
      id: u.id, username: u.username,
      firstName: u.first_name, lastName: u.last_name,
      fullName: [u.first_name, u.last_name].filter(Boolean).join(" ") || u.username,
    }));
  }

  // ===== Копирование задания =====

  async copyJob(id: string): Promise<Record<string, unknown>> {
    const source = await this.request<Record<string, unknown>>(`/api/v1/client/jobs/${id}`);
    const items = Array.isArray(source.items) ? (source.items as Array<Record<string, unknown>>) : [];
    return this.createJob({
      jobCategoryCode: Number(source.job_category_code ?? source.jobCategoryCode ?? 0),
      description: String(source.description ?? "") + " (копия)",
      customerName: source.customer_name ? String(source.customer_name) : undefined,
      customerId: source.customer_id ? String(source.customer_id) : undefined,
      warehouseId: source.warehouse_id ? String(source.warehouse_id) : undefined,
      items: items.map((it) => ({
        itemId: String(it.item_id ?? ""),
        planQty: Number(it.plan_qty ?? 0),
      })).filter((it) => it.itemId),
    });
  }

  // ===== Товары (CRUD) =====

  async createItem(data: {
    sku: string; name: string; description?: string; unit?: string;
    barcodes?: string[]; categoryId?: string; price?: number; costPrice?: number;
    minQuantity?: number; isActive?: boolean;
  }): Promise<ItemDto> {
    const raw = await this.request<ItemRaw>('/api/v1/client/items', {
      method: 'POST',
      body: JSON.stringify({
        sku: data.sku, name: data.name, description: data.description ?? null,
        unit: data.unit ?? 'шт', barcodes: data.barcodes ?? [], category_id: data.categoryId ?? null,
        price: data.price ?? 0, cost_price: data.costPrice ?? null,
        min_quantity: data.minQuantity ?? null, is_active: data.isActive ?? true,
      }),
    });
    return mapItem(raw);
  }

  async updateItem(id: string, data: {
    name?: string; description?: string; unit?: string; barcodes?: string[];
    categoryId?: string; price?: number; costPrice?: number; minQuantity?: number; isActive?: boolean;
  }): Promise<ItemDto> {
    const body: Record<string, unknown> = {};
    if (data.name !== undefined) body.name = data.name;
    if (data.description !== undefined) body.description = data.description;
    if (data.unit !== undefined) body.unit = data.unit;
    if (data.barcodes !== undefined) body.barcodes = data.barcodes;
    if (data.categoryId !== undefined) body.category_id = data.categoryId;
    if (data.price !== undefined) body.price = data.price;
    if (data.costPrice !== undefined) body.cost_price = data.costPrice;
    if (data.minQuantity !== undefined) body.min_quantity = data.minQuantity;
    if (data.isActive !== undefined) body.is_active = data.isActive;
    const raw = await this.request<ItemRaw>(`/api/v1/client/items/${id}`, { method: 'PUT', body: JSON.stringify(body) });
    return mapItem(raw);
  }

  async deleteItem(id: string): Promise<void> {
    await this.request(`/api/v1/client/items/${id}`, { method: 'DELETE' });
  }

  async importItems(items: Array<{
    sku: string; name: string; unit?: string; barcodes?: string[];
    price?: number; isActive?: boolean;
  }>): Promise<{ created: number; updated: number; skipped: number; errors: string[] }> {
    const raw = await this.request<{ created: number; updated: number; skipped: number; errors: string[] }>(
      '/api/v1/client/items/import',
      { method: 'POST', body: JSON.stringify(items) }
    );
    return { created: raw.created ?? 0, updated: raw.updated ?? 0, skipped: raw.skipped ?? 0, errors: raw.errors ?? [] };
  }

  // ===== Каталоги ТСД (CRUD) =====

  async upsertCatalogItem(data: { catalogKey: string; itemId: string; title?: string; code?: string; attributes?: Record<string, string | null> }): Promise<unknown> {
    return this.request('/api/v1/client/tsd-catalogs', {
      method: 'POST',
      body: JSON.stringify({
        catalog_key: data.catalogKey,
        item_id: data.itemId,
        title: data.title ?? null,
        code: data.code ?? null,
        attributes: data.attributes ?? null,
      }),
    });
  }

  async importCatalogItems(catalogKey: string, items: Array<{ itemId: string; title?: string; code?: string; attributes?: Record<string, string | null> }>): Promise<{ created: number; updated: number; skipped: number }> {
    const raw = await this.request<{ created: number; updated: number; skipped: number }>(
      '/api/v1/client/tsd-catalogs/import',
      { method: 'POST', body: JSON.stringify({ catalog_key: catalogKey, items }) }
    );
    return { created: raw.created ?? 0, updated: raw.updated ?? 0, skipped: raw.skipped ?? 0 };
  }

  async deleteCatalogItem(id: string): Promise<void> {
    await this.request(`/api/v1/client/tsd-catalogs/${id}`, { method: 'DELETE' });
  }

  // ===== Шаблоны печати =====

  async getPrintTemplates(type?: string): Promise<Array<Record<string, unknown>>> {
    const suffix = type ? `?type=${encodeURIComponent(type)}` : '';
    const raw = await this.request<{ templates: Array<Record<string, unknown>>; total: number }>(
      `/api/v1/printing/templates${suffix}`
    );
    return raw?.templates ?? [];
  }

  async getPrintTemplate(id: string): Promise<Record<string, unknown>> {
    return this.request(`/api/v1/printing/templates/${id}`);
  }

  async createPrintTemplate(data: {
    name: string; description?: string; type?: string; width: number; height: number;
    fields?: Array<Record<string, unknown>>; zplTemplate?: string; isDefault?: boolean; isActive?: boolean;
  }): Promise<Record<string, unknown>> {
    return this.request('/api/v1/printing/templates', {
      method: 'POST',
      body: JSON.stringify({
        name: data.name,
        description: data.description ?? null,
        type: data.type ?? 'product_label',
        width: data.width,
        height: data.height,
        fields: data.fields ?? [],
        zpl_template: data.zplTemplate ?? null,
        is_default: data.isDefault ?? false,
        is_active: data.isActive ?? true,
      }),
    });
  }

  async updatePrintTemplate(id: string, data: {
    name: string; description?: string; type?: string; width: number; height: number;
    fields?: Array<Record<string, unknown>>; zplTemplate?: string; isDefault?: boolean; isActive?: boolean;
  }): Promise<Record<string, unknown>> {
    return this.request(`/api/v1/printing/templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify({
        name: data.name,
        description: data.description ?? null,
        type: data.type ?? 'product_label',
        width: data.width,
        height: data.height,
        fields: data.fields ?? [],
        zpl_template: data.zplTemplate ?? null,
        is_default: data.isDefault ?? false,
        is_active: data.isActive ?? true,
      }),
    });
  }

  async deletePrintTemplate(id: string): Promise<void> {
    await this.request(`/api/v1/printing/templates/${id}`, { method: 'DELETE' });
  }

  async setDefaultPrintTemplate(id: string): Promise<void> {
    await this.request(`/api/v1/printing/templates/${id}/set-default`, { method: 'POST' });
  }

  // ===== Организация (настройки) =====

  async getOrganizationInfo(): Promise<{ id: string; name: string; bin?: string; email?: string; phone?: string; address?: string }> {
    const raw = await this.request<Record<string, unknown>>('/api/v1/client/settings/organization');
    return {
      id: String(raw?.id ?? raw?._id ?? ''),
      name: String(raw?.name ?? ''),
      bin: raw?.bin ? String(raw.bin) : undefined,
      email: raw?.email ? String(raw.email) : undefined,
      phone: raw?.phone ? String(raw.phone) : undefined,
      address: raw?.address ? String(raw.address) : undefined,
    };
  }

  async updateOrganizationInfo(data: { name?: string; email?: string | null; phone?: string | null; address?: string | null }): Promise<void> {
    await this.request('/api/v1/client/settings/organization', {
      method: 'PUT',
      body: JSON.stringify({
        name: data.name ?? null,
        email: data.email ?? null,
        phone: data.phone ?? null,
        address: data.address ?? null,
      }),
    });
  }

  // ===== Задания (редактирование) =====

  async updateJob(id: string, data: {
    description?: string; customerName?: string; customerId?: string; orderNumber?: string;
    warehouseId?: string; assignedToId?: string; priority?: number; dueAt?: string;
  }): Promise<Record<string, unknown>> {
    const body: Record<string, unknown> = { target_device_id: null };
    if (data.description !== undefined) body.description = data.description;
    if (data.customerName !== undefined) body.customer_name = data.customerName;
    if (data.customerId !== undefined) body.customer_id = data.customerId;
    if (data.assignedToId !== undefined) body.assigned_to_id = data.assignedToId;
    if (data.priority !== undefined) body.priority = data.priority;
    if (data.dueAt !== undefined) body.due_at = data.dueAt;
    return this.request(`/api/v1/client/jobs/${id}`, { method: 'PUT', body: JSON.stringify(body) });
  }

  async addJobItem(jobId: string, itemId: string, planQty: number): Promise<Record<string, unknown>> {
    return this.request(`/api/v1/client/jobs/${jobId}/items`, {
      method: 'POST',
      body: JSON.stringify({ item_id: itemId, plan_qty: planQty }),
    });
  }

  async removeJobItem(jobId: string, itemId: string): Promise<Record<string, unknown>> {
    return this.request(`/api/v1/client/jobs/${jobId}/items/${itemId}`, { method: 'DELETE' });
  }

  // ===== Push-уведомления =====

  async getDevices(): Promise<Array<{
    id: string;
    deviceId: string;
    deviceName: string;
    customName?: string | null;
    userId?: string | null;
    platform: string;
    isActive: boolean;
    lastSeenAt?: string | null;
    hasFcmToken: boolean;
  }>> {
    const raw = await this.request<{ devices: Array<{
      id: string; device_id: string; device_name: string; custom_name?: string | null;
      user_id?: string | null; platform: string; is_active: boolean;
      last_seen_at?: string | null; has_fcm_token: boolean;
    }> }>('/api/v1/tsd/devices');
    return (raw?.devices ?? []).map((d) => ({
      id: d.id,
      deviceId: d.device_id,
      deviceName: d.device_name,
      customName: d.custom_name ?? null,
      userId: d.user_id ?? null,
      platform: d.platform,
      isActive: d.is_active,
      lastSeenAt: d.last_seen_at ?? null,
      hasFcmToken: !!d.has_fcm_token,
    }));
  }

  async sendNotification(data: {
    deviceIds?: string[];
    userIds?: string[];
    topic?: string;
    title: string;
    body: string;
    data?: Record<string, string>;
  }): Promise<{ total: number; sent: number; failed: number; failures: Array<{ token: string; reason: string; shouldUnregister: boolean }>; disabledTokens: string[] }> {
    const raw = await this.request<{
      total: number; sent: number; failed: number;
      failures: Array<{ token: string; reason: string; should_unregister: boolean }>;
      disabled_tokens: string[];
    }>('/api/v1/client/notifications/send', {
      method: 'POST',
      body: JSON.stringify({
        device_ids: data.deviceIds ?? null,
        user_ids: data.userIds ?? null,
        topic: data.topic ?? null,
        title: data.title,
        body: data.body,
        data: data.data ?? null,
      }),
    });
    return {
      total: raw?.total ?? 0,
      sent: raw?.sent ?? 0,
      failed: raw?.failed ?? 0,
      failures: (raw?.failures ?? []).map((f) => ({
        token: f.token, reason: f.reason, shouldUnregister: !!f.should_unregister,
      })),
      disabledTokens: raw?.disabled_tokens ?? [],
    };
  }

  async getOrganizationSettings(): Promise<{
    pushEnabled: boolean;
    pushOnJobCreate: boolean;
    pushOnJobAssign: boolean;
    pushOnJobComplete: boolean;
  }> {
    const raw = await this.request<{
      push_enabled?: boolean;
      push_on_job_create?: boolean;
      push_on_job_assign?: boolean;
      push_on_job_complete?: boolean;
    }>('/api/v1/client/settings/organization/settings');
    return {
      pushEnabled: !!raw?.push_enabled,
      pushOnJobCreate: !!raw?.push_on_job_create,
      pushOnJobAssign: !!raw?.push_on_job_assign,
      pushOnJobComplete: !!raw?.push_on_job_complete,
    };
  }

  async updateOrganizationPushSettings(data: {
    pushEnabled?: boolean;
    pushOnJobCreate?: boolean;
    pushOnJobAssign?: boolean;
    pushOnJobComplete?: boolean;
  }): Promise<void> {
    const body: Record<string, unknown> = {};
    if (data.pushEnabled !== undefined) body.push_enabled = data.pushEnabled;
    if (data.pushOnJobCreate !== undefined) body.push_on_job_create = data.pushOnJobCreate;
    if (data.pushOnJobAssign !== undefined) body.push_on_job_assign = data.pushOnJobAssign;
    if (data.pushOnJobComplete !== undefined) body.push_on_job_complete = data.pushOnJobComplete;
    await this.request('/api/v1/client/settings/organization/settings', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }
}

// Синглтон API-клиента
export const api = new ApiClient();
